// The BFF returns camelCase for every payload (translating the API's
// snake_case internally) — so every type in this file matches that 1:1.
export interface InvoiceSummary {
  id: number;
  licenceId: number;
  facilityId: number | null;
  amount: number;
  billingPeriod: string;
  periodMonths: number | null;
  dueDate: string | null;
  status: string;
  // Set only when a rebate promotion discounted this invoice: amount before the
  // promotion (same period as amount), the promotion code and the rebate %.
  baseAmount?: number | null;
  promotionCode?: string | null;
  rebatePercentage?: number | null;
}

// MED-1652: an invoice of the facility's licence history
// (GET /healthcare-facilities/{facilityId}/invoices). Payment fields are set only
// once the invoice has a successful payment.
export interface FacilityInvoice extends InvoiceSummary {
  facilityName?: string | null;
  licenceType?: string | null;
  numberOfDoctors?: number | null;
  contractDuration?: string | null;
  paymentBillingMode?: string | null;
  paidDate?: string | null;
  createdAt?: string | null;
  paymentDate?: string | null;
  paymentMethod?: string | null;
  paymentReference?: string | null;
}

// The BFF's PageResponse (content plus page metadata, page number 0-based).
export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export type PaymentStatus =
  | "PENDING"
  | "INITIATED"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

export interface PaymentEvent {
  status: PaymentStatus;
  createdAt: string;
}

export interface PaymentInitiatedResponse {
  paymentIntentId: number;
  // Absent when initiate reused an open payment that turned out to be
  // already paid (status COMPLETED) — there is no checkout to redirect to.
  checkoutUrl?: string;
  providerToken?: string;
  provider: string;
  status: PaymentStatus;
  amount: number;
  currency: string;
}

// An unfinished checkout for an invoice that can be resumed instead of
// starting a new one (GET /invoices/{id}/payments/open).
export interface OpenPaymentIntent {
  paymentIntentId: number;
  checkoutUrl: string;
  status: PaymentStatus;
  amount: number;
  currency: string;
  createdAt: string;
}

export interface PaymentStatusResponse {
  paymentIntentId: number;
  invoiceId: number | null;
  provider: string;
  status: PaymentStatus;
  amount: number;
  currency: string;
  events: PaymentEvent[];
}

export const TERMINAL_STATUSES: PaymentStatus[] = [
  "COMPLETED",
  "FAILED",
  "CANCELLED",
];

export interface PaymentSessionContext {
  type: "facility";
  invoiceId: number;
  paymentIntentId: number;
}

export const PAYMENT_SESSION_KEY = "med360_payment_ctx";
